import { useEffect, useState } from "react";

import { SEGMENT_NAMES, SEGMENT_PRIORITY } from "@/constants";
import { UserContextType, CustomerCampaign } from "@/context/UserContext";

// Define the return type of the hook.
interface RewardsHookResult {
  campaigns: CustomerCampaign[] | null;
  active: CustomerCampaign[] | null;
  accepted: CustomerCampaign[] | null;
  nextReward: CustomerCampaign | { segment_name: string } | null;
}

/**
 * A custom hook to manage user reward campaigns.
 * It filters, sorts, and determines the next most relevant reward for a logged-in user.
 *
 * @param {UserContextType} userState The user's state, including login status and customer campaigns.
 * @returns {RewardsHookResult} An object containing all campaigns, active campaigns, accepted campaigns, and the next most relevant reward.
 */
export const useRewards = (userState: UserContextType): RewardsHookResult => {
  const [campaigns, setCampaigns] = useState<CustomerCampaign[] | null>(null);

  useEffect(() => {
    setCampaigns(userState.customerCampaigns);
  }, [userState]);

  let active: CustomerCampaign[] | null = null;
  let accepted: CustomerCampaign[] | null = null;
  let nextReward: CustomerCampaign | { segment_name: string } | null = null;

  if (userState.isLoggedIn && Array.isArray(campaigns)) {
    active = campaigns
      ?.filter((c) => !c.is_accepted)
      .sort((a, b) => {
        // Compare expiration days first (earliest first)
        if (a.remaining_lifetime < b.remaining_lifetime) return -1;
        if (a.remaining_lifetime > b.remaining_lifetime) return 1;

        // If the expiration days are the same, sort by the defined priority order
        return SEGMENT_PRIORITY.indexOf(a.segment_name) - SEGMENT_PRIORITY.indexOf(b.segment_name);
      });
    accepted = campaigns?.filter((c) => c.is_accepted);
    //In the event that there are multiple rewards  default to the most relevant one
    if (active.length > 0) {
      nextReward = active[0];
    } else if (accepted.length > 0) {
      //A banner to refer a friend shows up as soon as a reward (all rewards) have been activated
      nextReward = { segment_name: SEGMENT_NAMES.REFER_A_FRIEND };
    }
  }

  return { campaigns, active, accepted, nextReward };
};
